import React, { useCallback, useLayoutEffect, useRef, useState, } from 'react';
import { styled, Switch as MUISwitch } from '@mui/material';
import clsx from 'clsx';
import { getStyle, switchClasses } from './style';
import { IconLoading } from '../../internal/icons';
import { findAccessor } from '../../Shared/dom';
import { useMixedValue } from '../../hooks/useMixedValue';
const SwitchBase = React.forwardRef(({ checked, ...props }, ref) => (React.createElement(MUISwitch, { ref: ref, classes: switchClasses, ...props, checked: checked ?? false, size: "small" })));
const StyledSwitch = styled(SwitchBase)((props) => {
    const { theme, size = 'medium', loading } = props;
    return getStyle(theme, { size, loading: !!loading });
});
const Switch = React.forwardRef((props, ref) => {
    const { loading: _loading, inputProps, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, onKeyDown, onChange, autoLoading, defaultChecked, checked, ...restProps } = props;
    const [innerChecked, setInnerChecked] = useMixedValue(checked, !!defaultChecked);
    let { disabled } = props;
    const loadingIcons = {};
    const [innerLoading, setInnerLoading] = useState(false);
    const loading = _loading ?? innerLoading;
    const handleChange = useCallback(async (event, checked) => {
        if (disabled) {
            return;
        }
        if (autoLoading) {
            setInnerLoading(true);
        }
        try {
            await onChange?.(event, checked);
            setInnerChecked(checked);
        }
        catch (e) { }
        if (autoLoading) {
            setInnerLoading(false);
        }
    }, [autoLoading, disabled, onChange, setInnerChecked]);
    const passOnChange = useCallback((event, checked) => {
        void handleChange(event, checked);
    }, [handleChange]);
    if (loading === true) {
        disabled = true;
        Object.assign(loadingIcons, {
            icon: (React.createElement("span", { className: switchClasses.thumb },
                React.createElement(IconLoading, { className: "ui-Switch-loading-icon" }))),
            checkedIcon: (React.createElement("span", { className: switchClasses.thumb },
                React.createElement(IconLoading, { className: "ui-Switch-loading-icon" }))),
        });
    }
    // We have to remove the tabIndex for the switch base span element,
    // or Voice Over will announce an extra "group" for it.
    // But we cannot modify the behavior directly in MUI for disabled switches, see:
    // https://github.com/mui/material-ui/blob/master/packages/mui-material/src/ButtonBase/ButtonBase.js#L360
    // So we have to do this hacky work.
    const inputRef = useRef(null);
    useLayoutEffect(() => {
        /* istanbul ignore else */
        if (inputRef.current) {
            const base = findAccessor(inputRef.current, (t) => t.classList.contains(switchClasses.switchBase));
            /* istanbul ignore else */
            if (base) {
                base.removeAttribute('tabIndex');
            }
        }
    }, [disabled]);
    function handleKeyDown(e) {
        if (!disabled && e.key === 'Enter') {
            const event = new MouseEvent('click', {
                bubbles: true,
            });
            e.target.dispatchEvent(event);
            e.stopPropagation();
            e.preventDefault();
        }
        onKeyDown?.(e);
    }
    return (React.createElement(StyledSwitch, { className: clsx({ [switchClasses.loading]: loading }), ...restProps, disabled: disabled, inputProps: {
            role: 'switch',
            'aria-label': ariaLabel,
            'aria-labelledby': ariaLabelledBy,
            'aria-busy': loading,
            // According to AX requirements, the disabled state still needs to support focus.
            // By default, the input will not be disabled, but only disabled at the visual level.
            disabled: false,
            ...inputProps,
        }, 
        // The tabIndex does not take effect here in MUI
        // tabIndex={undefined}
        sx: {
            cursor: disabled ? 'not-allowed' : undefined,
            ...props.sx,
        }, onKeyDown: handleKeyDown, checked: innerChecked, onChange: passOnChange, ...loadingIcons, ref: ref, inputRef: inputRef }));
});
export default Switch;
